// apps/web/src/routes/assignment-rules.tsx
import { useState } from 'react'
import useSWR from 'swr'
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
  useSortable, arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@viecxanh/chat-ui/table'
import { Button } from '@viecxanh/chat-ui/button'
import { Badge } from '@viecxanh/chat-ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@viecxanh/chat-ui/dialog'
import { Input } from '@viecxanh/chat-ui/input'
import { Switch } from '@viecxanh/chat-ui/switch'
import { api } from '../lib/api-client'
import type { AssignmentRule } from '@viecxanh/chat-shared'

function SortableRow({ rule, onEdit, onDelete }: { rule: AssignmentRule; onEdit: (r: AssignmentRule) => void; onDelete: (id: string) => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: rule._id })
  return (
    <TableRow ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}>
      <TableCell>
        <span {...attributes} {...listeners} className="cursor-grab text-slate-400 mr-2">⠿</span>
        {rule.priority}
      </TableCell>
      <TableCell>{rule.name}</TableCell>
      <TableCell>{rule.channel_filter ?? 'Tất cả'}</TableCell>
      <TableCell>{rule.keyword_match ?? '—'}</TableCell>
      <TableCell>{rule.assigned_staff_name ?? '—'}</TableCell>
      <TableCell>
        <Badge className={rule.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>
          {rule.is_active ? 'Hoạt động' : 'Tắt'}
        </Badge>
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={() => onEdit(rule)}>Sửa</Button>
          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => onDelete(rule._id)}>Xóa</Button>
        </div>
      </TableCell>
    </TableRow>
  )
}

export function AssignmentRulesPage() {
  const { data, mutate } = useSWR<AssignmentRule[]>('/api/admin/assignment-rules', (url: string) => api.get<AssignmentRule[]>(url))
  const [rules, setRules] = useState<AssignmentRule[]>([])
  const [editing, setEditing] = useState<AssignmentRule | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ name: '', channel_filter: '', keyword_match: '', assigned_staff_id: '', is_active: true })

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const displayed = data ?? rules

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIdx = displayed.findIndex((r) => r._id === active.id)
    const newIdx = displayed.findIndex((r) => r._id === over.id)
    const reordered = arrayMove(displayed, oldIdx, newIdx)
    setRules(reordered)
    await api.put('/api/admin/assignment-rules/reorder', { ids: reordered.map((r) => r._id) })
    await mutate()
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (editing) {
      await api.put(`/api/admin/assignment-rules/${editing._id}`, form)
    } else {
      await api.post('/api/admin/assignment-rules', form)
    }
    await mutate()
    setShowForm(false)
    setEditing(null)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Xóa quy tắc này?')) return
    await api.delete(`/api/admin/assignment-rules/${id}`)
    await mutate()
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Quy tắc phân công</h1>
        <Button onClick={() => { setEditing(null); setForm({ name: '', channel_filter: '', keyword_match: '', assigned_staff_id: '', is_active: true }); setShowForm(true) }} className="bg-brand hover:bg-brand-dark">
          + Thêm quy tắc
        </Button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => void handleDragEnd(e)}>
        <SortableContext items={displayed.map((r) => r._id)} strategy={verticalListSortingStrategy}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ưu tiên</TableHead>
                <TableHead>Tên</TableHead>
                <TableHead>Kênh</TableHead>
                <TableHead>Từ khóa</TableHead>
                <TableHead>Nhân viên</TableHead>
                <TableHead>Trạng thái</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayed.map((rule) => (
                <SortableRow
                  key={rule._id}
                  rule={rule}
                  onEdit={(r) => { setEditing(r); setForm({ name: r.name, channel_filter: r.channel_filter ?? '', keyword_match: r.keyword_match ?? '', assigned_staff_id: r.assigned_staff_id ?? '', is_active: r.is_active }); setShowForm(true) }}
                  onDelete={(id) => void handleDelete(id)}
                />
              ))}
            </TableBody>
          </Table>
        </SortableContext>
      </DndContext>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Sửa quy tắc' : 'Thêm quy tắc'}</DialogTitle></DialogHeader>
          <form onSubmit={(e) => void handleSave(e)} className="space-y-3">
            <Input placeholder="Tên quy tắc" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <Input placeholder="Kênh (facebook, zalo_oa, ...)" value={form.channel_filter} onChange={(e) => setForm({ ...form, channel_filter: e.target.value })} />
            <Input placeholder="Từ khóa match" value={form.keyword_match} onChange={(e) => setForm({ ...form, keyword_match: e.target.value })} />
            <Input placeholder="ID nhân viên phụ trách" value={form.assigned_staff_id} onChange={(e) => setForm({ ...form, assigned_staff_id: e.target.value })} />
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <span className="text-sm">Hoạt động</span>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Hủy</Button>
              <Button type="submit" className="bg-brand hover:bg-brand-dark">Lưu</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
